<template>
  <div
    v-if="heroVideo"
    class="brand-hero-video"
    :class="{ 'is-playing': isPlaying }"
  >
    <!-- Video Player (when playing) -->
    <div
      v-if="isPlaying"
      class="brand-hero-video__player"
    >
      <button
        class="brand-hero-video__close"
        aria-label="Close video"
        @click="stopVideo"
      >
        <AkIcon symbol="x" />
      </button>

      <iframe
        ref="videoPlayer"
        title="brand hero video"
        :src="`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        class="brand-hero-video__iframe"
      />
    </div>

    <!-- Play Button Overlay (when not playing) -->
    <div
      v-else
      class="brand-hero-video__overlay"
      @click="playVideo"
    >
      <div class="brand-hero-video__play-button">
        <svg
          viewBox="1 0 24 24"
          fill="currentColor"
        >
          <polygon points="8,5 19,12 8,19" />
        </svg>
      </div>
      <div class="brand-hero-video__text">
        {{ t('brand.storefront.video.watchStory') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import { useVideoTracking } from '../../composables/use-video-tracking';
import { useI18n } from 'vue-i18n';
import { extractYouTubeVideoId } from '../../utils/extract-youtube-video-id';
import type { Video } from '../../types';
import { GetVideosParams1FilterDisplayContextEnum } from '@ankorstore/api-internal/data-contracts';

const props = defineProps<{
  heroVideo: Video;
  brandUuid: string;
}>();

const { t } = useI18n();
const isPlaying = ref(false);
const { trackPlay } = useVideoTracking({
  brandUuid: props.brandUuid,
  displayContext: GetVideosParams1FilterDisplayContextEnum.Hero,
});


const videoId = computed(() => extractYouTubeVideoId(props.heroVideo.attributes.videoUrl));

const playVideo = () => {
  isPlaying.value = true;
  trackPlay(videoId.value);
};

const stopVideo = () => {
  isPlaying.value = false;
};

// Handle ESC key
const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isPlaying.value) {
    stopVideo();
  }
};

// Listen for ESC key
if (globalThis.window !== undefined) {
  globalThis.window.addEventListener('keydown', handleEscape);
}

onBeforeUnmount(() => {
  if (globalThis.window !== undefined) {
    globalThis.window.removeEventListener('keydown', handleEscape);
  }
});
</script>

<style scoped lang="scss">
.brand-hero-video {
  @apply ds-absolute ds-inset-0 ds-z-10;

  &__overlay {
    @apply ds-absolute ds-inset-0;
    @apply ds-flex ds-flex-col ds-items-center ds-justify-center ds-gap-4;
    @apply ds-cursor-pointer;
    @apply ds-transition-colors ds-duration-300;
    background: rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(0, 0, 0, 0.4);

      .brand-hero-video__play-button {
        @apply ds-scale-110;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
      }
    }
  }

  &__play-button {
    @apply ds-w-[96px] ds-h-[96px];
    @apply ds-rounded-full ds-bg-white;
    @apply ds-flex ds-items-center ds-justify-center;
    @apply ds-transition-all ds-duration-200;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);

    svg {
      @apply ds-w-[40px] ds-h-[40px];
      @apply ds-ml-[6px];
      color: var(--primary);
    }
  }

  &__text {
    @apply ds-text-white ds-text-xl ds-font-semibold;
    @apply ds-tracking-wide;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  &__player {
    @apply ds-absolute ds-inset-0;
    background: var(--primary);
    animation: fadeIn 0.3s ease;
  }

  &__close {
    @apply ds-absolute ds-top-[16px] ds-right-[16px];
    @apply ds-z-20;
    @apply ds-w-[48px] ds-h-[48px];
    @apply ds-rounded-full;
    @apply ds-flex ds-items-center ds-justify-center;
    @apply ds-cursor-pointer;
    @apply ds-transition-all ds-duration-200;
    background: rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: var(--white);

    &:hover {
      @apply ds-scale-105;
      background: rgba(0, 0, 0, 0.9);
      border-color: rgba(255, 255, 255, 0.6);
    }

    svg {
      @apply ds-w-[24px] ds-h-[24px];
    }
  }

  &__iframe {
    @apply ds-w-full ds-h-full;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
